import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import CommandCenterPage from "@/pages/CommandCenterPage";
import InvoicesPage from "@/pages/InvoicesPage";
import InvoiceReviewPage from "@/pages/InvoiceReviewPage";
import ExpensesPage from "@/pages/ExpensesPage";
import PurchasesPage from "@/pages/PurchasesPage";
import PurchaseImporterPage from "@/pages/PurchaseImporterPage";
import ExpenseImporterPage from "@/pages/ExpenseImporterPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientImporterPage from "@/pages/ClientImporterPage";
import ItemsServicesPage from "@/pages/ItemsServicesPage";
import ItemsImporterPage from "@/pages/ItemsImporterPage";
import ReceivablesPage from "@/pages/ReceivablesPage";
import ReceiptsPage from "@/pages/ReceiptsPage";
import PayablesPage from "@/pages/PayablesPage";
import PaymentsPage from "@/pages/PaymentsPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import VendorsPage from "@/pages/VendorsPage";
import BusinessSettingsPage from "@/pages/BusinessSettingsPage";
import ReportsPage from "@/pages/ReportsPage";
import SalesImporterPage from "@/pages/SalesImporterPage";
import AccessRestrictedPage from "@/pages/AccessRestrictedPage";
import AdminPortalPage from "@/pages/AdminPortalPage";
import LegalPage from "@/pages/LegalPage";
import { AppLayout } from "@/components/AppLayout";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  if (!user.home_state && location.pathname !== "/onboarding" && location.pathname !== "/akupara-admin") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

function AppContent() {
  const location = useLocation();

  // CRITICAL: Check for OAuth callback synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><DashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/command" element={<ProtectedRoute><AppLayout><CommandCenterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><AppLayout><InvoicesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/invoices/:id/review" element={<ProtectedRoute><AppLayout><InvoiceReviewPage /></AppLayout></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><AppLayout><ExpensesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/purchases" element={<ProtectedRoute><AppLayout><PurchasesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/purchases/import" element={<ProtectedRoute><AppLayout><PurchaseImporterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/expenses/import" element={<ProtectedRoute><AppLayout><ExpenseImporterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><AppLayout><ClientsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/clients/import" element={<ProtectedRoute><AppLayout><ClientImporterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/items" element={<ProtectedRoute><AppLayout><ItemsServicesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/items/import" element={<ProtectedRoute><AppLayout><ItemsImporterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/receivables" element={<ProtectedRoute><AppLayout><ReceivablesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><AppLayout><ReceiptsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/payables" element={<ProtectedRoute><AppLayout><PayablesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><AppLayout><PaymentsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute><AppLayout><PurchaseOrdersPage /></AppLayout></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute><AppLayout><VendorsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><BusinessSettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><AppLayout><ReportsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/invoices/import" element={<ProtectedRoute><AppLayout><SalesImporterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/access-restricted" element={<AccessRestrictedPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/akupara-admin" element={<ProtectedRoute><AdminPortalPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
